import { HttpErrorResponse } from '@angular/common/http';
import { Injectable, computed, effect, inject, signal } from '@angular/core';
import { I18nService } from '../../../core';
import { OperationsApiService } from '../../../core/api';
import { CompanyUsersService } from '../../../core/services/company-users.service';
import { TenantService } from '../../../core/tenant/tenant.service';
import type { CustomFieldViewModel } from '../../custom-fields/models/custom-field.model';
import type { AutomationTriggerFieldOption } from '../models/automation-trigger-field.model';
import {
  NATIVE_CARD_FIELD_SELECT_PREFIX,
  NATIVE_CARD_FIELD_PLACEHOLDER_ID,
} from '../models/automation-trigger-field.model';
import {
  WorkflowAutomationConditionsApiService,
  WorkflowAutomationsApiService,
} from '../api';
import { EmailTemplatesApiService } from '../../company-communication/services/email-templates-api.service';
import { OnboardingChecklistStore } from '../../onboarding/stores/onboarding-checklist.store';
import type {
  AutomationExecutionListModel,
  AutomationPlaceholdersResponse,
  CreateWorkflowAutomationConditionRequestModel,
  CreateWorkflowAutomationRequestModel,
  EmailTemplateRefModel,
  UpdateWorkflowAutomationRequestModel,
  WhatsAppTemplateModel,
  WorkflowAutomationConditionModel,
  WorkflowAutomationModel,
} from '../models/automation-api.models';
import { actionLabel, isConditionValid, triggerLabel } from '../utils/automation-display.utils';

interface WorkflowStageOption {
  id: string;
  name: string;
}

interface MemberOption {
  id: string;
  name: string;
  email?: string;
}

@Injectable({ providedIn: 'root' })
export class AutomationsStore {
  private readonly i18n = inject(I18nService);
  private readonly tenant = inject(TenantService);
  private readonly automationsApi = inject(WorkflowAutomationsApiService);
  private readonly emailTemplatesApi = inject(EmailTemplatesApiService);
  private readonly conditionsApi = inject(WorkflowAutomationConditionsApiService);
  private readonly operationsApi = inject(OperationsApiService);
  private readonly companyUsers = inject(CompanyUsersService);
  private readonly checklistStore = inject(OnboardingChecklistStore);

  private readonly workflowIdSignal = signal<string | null>(null);
  private readonly loadedCompanyIdSignal = signal<string | null>(null);
  private readonly automationsSignal = signal<WorkflowAutomationModel[]>([]);
  private readonly selectedAutomationIdSignal = signal<string | null>(null);
  private readonly conditionsSignal = signal<WorkflowAutomationConditionModel[]>([]);
  private readonly conditionCountByAutomationSignal = signal<Record<string, number>>({});
  private readonly invalidConditionCountByAutomationSignal = signal<Record<string, number>>({});

  private readonly workflowStagesSignal = signal<WorkflowStageOption[]>([]);
  private readonly workflowFieldsSignal = signal<AutomationTriggerFieldOption[]>([]);
  private readonly membersSignal = signal<MemberOption[]>([]);
  private readonly supportsWhatsAppActionSignal = signal(false);
  private readonly whatsAppTemplatesSignal = signal<WhatsAppTemplateModel[]>([]);
  private readonly supportsEmailTemplateActionSignal = signal(false);
  private readonly emailTemplatesSignal = signal<EmailTemplateRefModel[]>([]);

  private readonly placeholdersSignal = signal<AutomationPlaceholdersResponse | null>(null);
  private readonly placeholdersCachedWorkflowIdSignal = signal<string | null>(null);

  private readonly executionsSignal = signal<AutomationExecutionListModel | null>(null);
  private readonly loadingExecutionsSignal = signal(false);

  private readonly loadingAutomationsSignal = signal(false);
  private readonly loadingConditionsSignal = signal(false);
  private readonly savingAutomationSignal = signal(false);
  private readonly savingConditionSignal = signal(false);
  private readonly deletingAutomationSignal = signal(false);
  private readonly deletingConditionSignal = signal(false);

  private readonly errorAutomationsSignal = signal<string | null>(null);
  private readonly errorConditionsSignal = signal<string | null>(null);

  readonly workflowId = this.workflowIdSignal.asReadonly();
  readonly automations = this.automationsSignal.asReadonly();
  readonly selectedAutomationId = this.selectedAutomationIdSignal.asReadonly();
  readonly conditions = this.conditionsSignal.asReadonly();
  readonly conditionCountByAutomation = this.conditionCountByAutomationSignal.asReadonly();
  readonly invalidConditionCountByAutomation =
    this.invalidConditionCountByAutomationSignal.asReadonly();
  readonly workflowStages = this.workflowStagesSignal.asReadonly();
  readonly workflowFields = this.workflowFieldsSignal.asReadonly();
  readonly members = this.membersSignal.asReadonly();
  readonly supportsWhatsAppAction = this.supportsWhatsAppActionSignal.asReadonly();
  readonly whatsAppTemplates = this.whatsAppTemplatesSignal.asReadonly();
  readonly supportsEmailTemplateAction = this.supportsEmailTemplateActionSignal.asReadonly();
  readonly emailTemplates = this.emailTemplatesSignal.asReadonly();

  readonly placeholders = this.placeholdersSignal.asReadonly();

  readonly executions = this.executionsSignal.asReadonly();
  readonly loadingExecutions = this.loadingExecutionsSignal.asReadonly();

  readonly loadingAutomations = this.loadingAutomationsSignal.asReadonly();
  readonly loadingConditions = this.loadingConditionsSignal.asReadonly();
  readonly savingAutomation = this.savingAutomationSignal.asReadonly();
  readonly savingCondition = this.savingConditionSignal.asReadonly();
  readonly deletingAutomation = this.deletingAutomationSignal.asReadonly();
  readonly deletingCondition = this.deletingConditionSignal.asReadonly();
  readonly errorAutomations = this.errorAutomationsSignal.asReadonly();
  readonly errorConditions = this.errorConditionsSignal.asReadonly();

  readonly selectedAutomation = computed(() => {
    const selectedId = this.selectedAutomationIdSignal();
    if (!selectedId) return null;
    return this.automations().find((item) => item.id === selectedId) ?? null;
  });

  readonly sortedAutomations = computed(() =>
    [...this.automations()].sort((a, b) => {
      if (a.isActive !== b.isActive) return a.isActive ? -1 : 1;
      return a.name.localeCompare(b.name);
    })
  );
  readonly activeAutomations = computed(() => this.automations().filter((item) => item.isActive));
  readonly inactiveAutomations = computed(() => this.automations().filter((item) => !item.isActive));
  readonly selectedAutomationConditions = computed(() => {
    const selected = this.selectedAutomationIdSignal();
    if (!selected) return [];
    return this.conditions().filter((item) => item.workflowAutomationId === selected);
  });
  readonly hasAutomations = computed(
    () => !this.loadingAutomations() && this.sortedAutomations().length > 0
  );
  readonly isEmpty = computed(
    () => !this.loadingAutomations() && !this.errorAutomations() && this.sortedAutomations().length === 0
  );
  readonly canEditSelectedAutomation = computed(() => !!this.selectedAutomation());
  readonly selectedAutomationDisplayTriggerLabel = computed(() => {
    const automation = this.selectedAutomation();
    if (!automation) return '';
    return triggerLabel(this.i18n, automation.triggerType);
  });
  readonly selectedAutomationDisplayActionLabel = computed(() => {
    const automation = this.selectedAutomation();
    if (!automation) return '';
    return actionLabel(this.i18n, automation.actionType);
  });
  readonly selectedAutomationDisplaySummary = computed(() => {
    const trigger = this.selectedAutomationDisplayTriggerLabel();
    const action = this.selectedAutomationDisplayActionLabel();
    if (!trigger || !action) return '';
    return this.i18n.t('features.automations.summary.pattern', { trigger, action });
  });
  readonly selectedAutomationDisplayConditionsSummary = computed(() => {
    const count = this.selectedAutomationConditions().length;
    const selectedId = this.selectedAutomationIdSignal();
    const invalidCount = selectedId ? this.invalidConditionCountByAutomation()[selectedId] ?? 0 : 0;
    const base = this.i18n.t('features.automations.conditions.count', { count });
    if (!invalidCount) return base;
    return `${base} · ${this.i18n.t('features.automations.conditions.invalidcount', { count: invalidCount })}`;
  });

  constructor() {
    effect(() => {
      const companyId = this.tenant.companyId();
      if (!companyId) {
        this.loadedCompanyIdSignal.set(null);
        this.reset();
        return;
      }
      if (this.loadedCompanyIdSignal() !== companyId) {
        this.loadedCompanyIdSignal.set(companyId);
        this.reset();
      }
    });
  }

  async loadAutomations(workflowId: string): Promise<void> {
    this.workflowIdSignal.set(workflowId);
    this.loadingAutomationsSignal.set(true);
    this.errorAutomationsSignal.set(null);
    console.log('[Automations] loadAutomations called for workflow:', workflowId);
    try {
      await this.companyUsers.ensureLoaded();
      const [automationsRes, stagesRes, fieldsRes, templatesRes, emailTplRes, placeholdersRes] = await Promise.all([
        this.automationsApi.listByWorkflow(workflowId),
        this.operationsApi.getWorkflowStages(workflowId),
        this.operationsApi.getWorkflowFields(workflowId),
        this.loadWhatsAppTemplates(),
        this.loadEmailTemplates(),
        this.loadPlaceholders(workflowId),
      ]);
      const hydratedAutomations = await this.hydrateMissingTriggerConfigs(
        workflowId,
        automationsRes.items
      );
      this.automationsSignal.set(hydratedAutomations);
      this.workflowStagesSignal.set(
        stagesRes.map((stage) => ({ id: stage.id, name: stage.name }))
      );
      this.workflowFieldsSignal.set(this.mapWorkflowFields(fieldsRes));
      this.membersSignal.set(
        this.companyUsers.users().map((u) => ({
          id: u.id,
          name: u.name,
          email: u.email || undefined,
        }))
      );
      this.whatsAppTemplatesSignal.set(templatesRes);
      this.supportsWhatsAppActionSignal.set(
        templatesRes.length > 0 ||
          automationsRes.items.some((item) => item.actionType === 'SendWhatsAppTemplate')
      );
      this.emailTemplatesSignal.set(emailTplRes);
      this.supportsEmailTemplateActionSignal.set(
        emailTplRes.length > 0 ||
          automationsRes.items.some((item) => item.actionType === 'SendEmailTemplate')
      );
      console.log('[Automations] Placeholders result:', placeholdersRes);
      this.placeholdersSignal.set(placeholdersRes);
      this.placeholdersCachedWorkflowIdSignal.set(workflowId);
      const selected = this.selectedAutomationIdSignal();
      if (selected && !automationsRes.items.some((item) => item.id === selected)) {
        this.selectedAutomationIdSignal.set(null);
      }
      this.conditionsSignal.set([]);
    } catch (error) {
      this.errorAutomationsSignal.set(
        this.readApiError(error, this.i18n.t('features.automations.error.loadfailed'))
      );
      this.automationsSignal.set([]);
    } finally {
      this.loadingAutomationsSignal.set(false);
    }
  }

  async selectAutomation(automationId: string | null): Promise<void> {
    this.selectedAutomationIdSignal.set(automationId);
    this.conditionsSignal.set([]);
  }

  /**
   * Carrega detalhe completo da automação (getById) + condições separadas.
   * Garante que campos de delay e condições estejam disponíveis para edição.
   */
  async loadAutomationForEdit(automationId: string): Promise<void> {
    const workflowId = this.workflowIdSignal();
    if (!workflowId) return;
    this.selectedAutomationIdSignal.set(automationId);
    this.conditionsSignal.set([]);
    try {
      const [detail, conditionsRes] = await Promise.all([
        this.automationsApi.getById(workflowId, automationId),
        this.conditionsApi.list(workflowId, automationId).catch(() => ({ items: [] as import('../models/automation-api.models').WorkflowAutomationConditionModel[] })),
      ]);
      if (detail) {
        this.automationsSignal.update((current) =>
          current.map((item) => (item.id === automationId ? detail : item))
        );
      }
      // Usa condições do endpoint dedicado; se vazio, fallback para condições inline do modelo
      const rawConditions = conditionsRes.items.length > 0
        ? conditionsRes.items
        : (detail?.conditions ?? []);
      const hydratedDetail = detail
        ? this.mergeTriggerConfigFromConditions(detail, rawConditions)
        : detail;
      const triggerType = hydratedDetail?.triggerType ?? detail?.triggerType ?? null;
      const triggerConfigJson = hydratedDetail?.triggerConfigJson ?? detail?.triggerConfigJson ?? null;
      const effectiveConditions = triggerType
        ? rawConditions.filter(
            (c) => !this.isTriggerManagedCondition(triggerType, triggerConfigJson, c)
          )
        : rawConditions;
      this.conditionsSignal.set(effectiveConditions);
      this.conditionCountByAutomationSignal.update((current) => ({
        ...current,
        [automationId]: effectiveConditions.length,
      }));
      this.invalidConditionCountByAutomationSignal.update((current) => ({
        ...current,
        [automationId]: this.countInvalidConditions(effectiveConditions),
      }));
      if (hydratedDetail) {
        this.automationsSignal.update((current) =>
          current.map((item) => (item.id === automationId ? hydratedDetail : item))
        );
      }
    } catch (error) {
      this.errorAutomationsSignal.set(
        this.readApiError(error, this.i18n.t('features.automations.error.loadfailed'))
      );
    }
  }

  async createAutomation(payload: CreateWorkflowAutomationRequestModel): Promise<boolean> {
    const workflowId = this.workflowIdSignal();
    if (!workflowId) return false;
    this.savingAutomationSignal.set(true);
    this.errorAutomationsSignal.set(null);
    try {
      const created = await this.automationsApi.create(workflowId, payload);
      const existingConditionsResponse = await this.conditionsApi
        .list(workflowId, created.id)
        .catch(() => ({ items: [] as WorkflowAutomationConditionModel[] }));
      const syncedConditions = await this.syncAutomationConditions(
        workflowId,
        created.id,
        existingConditionsResponse.items,
        payload.triggerType,
        payload.triggerConfigJson ?? null,
        payload.conditions ?? []
      );
      const createdWithConditions = this.mergeTriggerConfigFromConditions(
        syncedConditions.length > 0 || (payload.conditions?.length ?? 0) > 0
          ? { ...created, conditions: syncedConditions }
          : created,
        syncedConditions
      );
      this.automationsSignal.update((current) => [...current, createdWithConditions]);
      void this.checklistStore.refresh();
      return true;
    } catch (error) {
      this.errorAutomationsSignal.set(
        this.readApiError(error, this.i18n.t('features.automations.error.savefailed'))
      );
      return false;
    } finally {
      this.savingAutomationSignal.set(false);
    }
  }

  async updateAutomation(
    automationId: string,
    payload: UpdateWorkflowAutomationRequestModel
  ): Promise<boolean> {
    const workflowId = this.workflowIdSignal();
    if (!workflowId) return false;
    this.savingAutomationSignal.set(true);
    this.errorAutomationsSignal.set(null);
    try {
      const updated = await this.automationsApi.update(workflowId, automationId, payload);
      // Busca condições DEPOIS do PUT para obter os IDs atuais do servidor
      const currentConditionsResponse = await this.conditionsApi
        .list(workflowId, automationId)
        .catch(() => ({ items: [] as WorkflowAutomationConditionModel[] }));
      const syncedConditions = await this.syncAutomationConditions(
        workflowId,
        automationId,
        currentConditionsResponse.items,
        payload.triggerType,
        payload.triggerConfigJson ?? null,
        payload.conditions ?? []
      );
      const updatedWithConditions = this.mergeTriggerConfigFromConditions(
        {
          ...updated,
          conditions: syncedConditions,
        },
        syncedConditions
      );
      this.automationsSignal.update((current) =>
        current.map((item) => (item.id === automationId ? updatedWithConditions : item))
      );
      if (this.selectedAutomationIdSignal() === automationId) {
        this.conditionsSignal.set(syncedConditions);
        this.conditionCountByAutomationSignal.update((current) => ({
          ...current,
          [automationId]: syncedConditions.length,
        }));
        this.invalidConditionCountByAutomationSignal.update((current) => ({
          ...current,
          [automationId]: this.countInvalidConditions(syncedConditions),
        }));
      }
      return true;
    } catch (error) {
      this.errorAutomationsSignal.set(
        this.readApiError(error, this.i18n.t('features.automations.error.savefailed'))
      );
      return false;
    } finally {
      this.savingAutomationSignal.set(false);
    }
  }

  async toggleAutomationStatus(automationId: string, isActive: boolean): Promise<boolean> {
    const workflowId = this.workflowIdSignal();
    if (!workflowId) return false;
    this.savingAutomationSignal.set(true);
    this.errorAutomationsSignal.set(null);
    try {
      const updated = await this.automationsApi.toggle(workflowId, automationId, { isActive });
      if (updated) {
        this.automationsSignal.update((current) =>
          current.map((item) => (item.id === automationId ? updated : item))
        );
      } else {
        this.automationsSignal.update((current) =>
          current.map((item) => (item.id === automationId ? { ...item, isActive } : item))
        );
      }
      return true;
    } catch (error) {
      this.errorAutomationsSignal.set(
        this.readApiError(error, this.i18n.t('features.automations.error.savefailed'))
      );
      return false;
    } finally {
      this.savingAutomationSignal.set(false);
    }
  }

  async deleteAutomation(automationId: string): Promise<boolean> {
    const workflowId = this.workflowIdSignal();
    if (!workflowId) return false;
    this.deletingAutomationSignal.set(true);
    this.errorAutomationsSignal.set(null);
    try {
      await this.automationsApi.delete(workflowId, automationId);
      this.automationsSignal.update((current) => current.filter((item) => item.id !== automationId));
      if (this.selectedAutomationIdSignal() === automationId) {
        this.selectedAutomationIdSignal.set(null);
        this.conditionsSignal.set([]);
      }
      return true;
    } catch (error) {
      this.errorAutomationsSignal.set(
        this.readApiError(error, this.i18n.t('features.automations.error.deletefailed'))
      );
      return false;
    } finally {
      this.deletingAutomationSignal.set(false);
    }
  }

  async loadConditions(automationId: string): Promise<void> {
    const workflowId = this.workflowIdSignal();
    if (!workflowId) return;
    this.loadingConditionsSignal.set(true);
    this.errorConditionsSignal.set(null);
    try {
      const response = await this.conditionsApi.list(workflowId, automationId);
      this.conditionsSignal.set(response.items);
      this.conditionCountByAutomationSignal.update((current) => ({
        ...current,
        [automationId]: response.items.length,
      }));
      this.invalidConditionCountByAutomationSignal.update((current) => ({
        ...current,
        [automationId]: this.countInvalidConditions(response.items),
      }));
    } catch (error) {
      this.errorConditionsSignal.set(
        this.readApiError(error, this.i18n.t('features.automations.error.loadconditionsfailed'))
      );
      this.conditionsSignal.set([]);
    } finally {
      this.loadingConditionsSignal.set(false);
    }
  }

  async createCondition(payload: CreateWorkflowAutomationConditionRequestModel): Promise<boolean> {
    const workflowId = this.workflowIdSignal();
    const automationId = this.selectedAutomationIdSignal();
    if (!workflowId || !automationId) return false;
    if (this.selectedAutomationConditions().length > 0) {
      this.errorConditionsSignal.set(
        this.i18n.t('features.automations.validation.singleConditionOnly')
      );
      return false;
    }

    this.savingConditionSignal.set(true);
    this.errorConditionsSignal.set(null);
    try {
      const created = await this.conditionsApi.create(workflowId, automationId, payload);
      if (created) {
        const nextConditions = [...this.conditionsSignal(), created];
        this.conditionsSignal.set(nextConditions);
        this.conditionCountByAutomationSignal.update((current) => ({
          ...current,
          [automationId]: (current[automationId] ?? 0) + 1,
        }));
        this.invalidConditionCountByAutomationSignal.update((current) => ({
          ...current,
          [automationId]: this.countInvalidConditions(nextConditions),
        }));
      } else {
        await this.loadConditions(automationId);
      }
      return true;
    } catch (error) {
      this.errorConditionsSignal.set(
        this.readApiError(error, this.i18n.t('features.automations.error.saveconditionfailed'))
      );
      return false;
    } finally {
      this.savingConditionSignal.set(false);
    }
  }

  async deleteCondition(conditionId: string): Promise<boolean> {
    const workflowId = this.workflowIdSignal();
    const automationId = this.selectedAutomationIdSignal();
    if (!workflowId || !automationId) return false;
    this.deletingConditionSignal.set(true);
    this.errorConditionsSignal.set(null);
    try {
      await this.conditionsApi.delete(workflowId, automationId, conditionId);
      const nextConditions = this.conditionsSignal().filter((item) => item.id !== conditionId);
      this.conditionsSignal.set(nextConditions);
      this.conditionCountByAutomationSignal.update((current) => ({
        ...current,
        [automationId]: Math.max((current[automationId] ?? 1) - 1, 0),
      }));
      this.invalidConditionCountByAutomationSignal.update((current) => ({
        ...current,
        [automationId]: this.countInvalidConditions(nextConditions),
      }));
      return true;
    } catch (error) {
      this.errorConditionsSignal.set(
        this.readApiError(error, this.i18n.t('features.automations.error.deleteconditionfailed'))
      );
      return false;
    } finally {
      this.deletingConditionSignal.set(false);
    }
  }

  async loadExecutions(automationId: string): Promise<void> {
    const workflowId = this.workflowIdSignal();
    if (!workflowId) return;
    this.loadingExecutionsSignal.set(true);
    try {
      const result = await this.automationsApi.getExecutionLogs(workflowId, automationId);
      this.executionsSignal.set(result);
    } catch {
      this.executionsSignal.set(null);
    } finally {
      this.loadingExecutionsSignal.set(false);
    }
  }

  clearError(): void {
    this.errorAutomationsSignal.set(null);
    this.errorConditionsSignal.set(null);
  }

  reset(): void {
    this.workflowIdSignal.set(null);
    this.automationsSignal.set([]);
    this.selectedAutomationIdSignal.set(null);
    this.conditionsSignal.set([]);
    this.conditionCountByAutomationSignal.set({});
    this.invalidConditionCountByAutomationSignal.set({});
    this.placeholdersSignal.set(null);
    this.placeholdersCachedWorkflowIdSignal.set(null);
    this.executionsSignal.set(null);
    this.loadingExecutionsSignal.set(false);
    this.workflowStagesSignal.set([]);
    this.workflowFieldsSignal.set([]);
    this.membersSignal.set([]);
    this.supportsWhatsAppActionSignal.set(false);
    this.whatsAppTemplatesSignal.set([]);
    this.supportsEmailTemplateActionSignal.set(false);
    this.emailTemplatesSignal.set([]);
    this.loadingAutomationsSignal.set(false);
    this.loadingConditionsSignal.set(false);
    this.savingAutomationSignal.set(false);
    this.savingConditionSignal.set(false);
    this.deletingAutomationSignal.set(false);
    this.deletingConditionSignal.set(false);
    this.errorAutomationsSignal.set(null);
    this.errorConditionsSignal.set(null);
  }

  private mapWorkflowFields(raw: unknown[]): AutomationTriggerFieldOption[] {
    return raw.flatMap((entry, index) => {
      const row = this.asRecord(entry);
        const nestedField = this.asRecord(row['field'] ?? row['Field']);
        const merged = { ...nestedField, ...row };

        const sourceKindRaw = this.coalesceTrimmedString(
          merged['sourceKind'],
          merged['SourceKind']
        );
        const isNative =
          sourceKindRaw.replace(/\s+/g, '').toLowerCase() === 'nativecardfield';

        const nativeCardFieldKey = this.coalesceTrimmedString(
          merged['nativeCardFieldKey'],
          merged['NativeCardFieldKey']
        );

        const rawId = this.coalesceTrimmedString(
          merged['id'],
          merged['fieldId'],
          merged['workflowFieldId'],
          merged['WorkflowFieldId']
        );

        const name = this.coalesceTrimmedString(
          merged['label'],
          merged['Label'],
          merged['name'],
          merged['Name'],
          merged['key'],
          merged['Key']
        );

        const rawFt = merged['fieldType'] ?? merged['FieldType'];
        let fieldTypeNum =
          typeof rawFt === 'number' && Number.isFinite(rawFt)
            ? rawFt
            : typeof rawFt === 'string' && rawFt.trim()
              ? Number(rawFt.trim())
              : NaN;
        if (!Number.isFinite(fieldTypeNum)) fieldTypeNum = NaN;
        const typeFromFieldTypeString =
          typeof rawFt === 'string' && rawFt.trim() && Number.isNaN(Number(rawFt.trim()))
            ? this.normalizeFieldType(rawFt.trim())
            : null;
        const typeFromString = this.normalizeFieldType(this.asString(merged['type'], ''));
        const type: CustomFieldViewModel['type'] = Number.isFinite(fieldTypeNum)
          ? this.fieldTypeFromApiNumeric(fieldTypeNum)
          : typeFromFieldTypeString || typeFromString || 'TEXT';

        const base: Omit<AutomationTriggerFieldOption, 'triggerSelectValue' | 'sourceKind'> = {
          fieldId: '',
          key: this.asNullableString(merged['key']) ?? this.asNullableString(merged['Key']),
          name: name || rawId || nativeCardFieldKey,
          type,
          required:
            this.readBooleanLike(merged['isRequired']) ||
            this.readBooleanLike(merged['IsRequired']) ||
            this.asBoolean(merged['required']),
          readOnly:
            this.readBooleanLike(merged['isReadOnly']) ||
            this.readBooleanLike(merged['IsReadOnly']) ||
            this.asBoolean(merged['readOnly']),
          showInQuickView:
            this.readBooleanLike(merged['showInQuickView']) ||
            this.readBooleanLike(merged['ShowInQuickView']) ||
            this.asBoolean(merged['showInQuickView']),
          showInApp:
            this.readBooleanLike(merged['showInApp']) ||
            this.readBooleanLike(merged['ShowInApp']) ||
            this.asBoolean(merged['showInApp']),
          options: this.asArray(merged['options']).map((option, optionIndex) => {
            const source = this.asRecord(option);
            return {
              id: this.asString(source['id']) || `opt-${optionIndex}`,
              label: this.asString(source['label']) || this.asString(source['value']),
              value: this.asString(source['value']) || this.asString(source['label']),
              colorHex: this.normalizeColorHex(this.asString(source['colorHex'])),
              order: this.asNumber(source['order'], optionIndex + 1),
            };
          }),
          order: this.asNumber(merged['order'], index + 1),
          placeholder: this.asNullableString(merged['placeholder']),
          formulaDefinition: null,
          formulaResultDecimalPlaces: 0,
          numberDecimalPlaces: 0,
          numberShowPercentSuffix: false,
          ratingLevels: 5,
          codePrefix: '',
          codeFormat: 'NUMERIC',
          codeLength: 4,
          codeSeparator: 'NONE',
          projectReusableFieldId: null,
          fromProjectReusable: false,
          personRestrictToWorkflowMembers: false,
          collectViaWhatsApp: false,
          whatsAppPrompt: null,
          whatsAppCollectionOrder: null,
        };

        if (isNative && nativeCardFieldKey) {
          const triggerSelectValue = `${NATIVE_CARD_FIELD_SELECT_PREFIX}${nativeCardFieldKey}`;
          return [
            {
              ...base,
              fieldId: triggerSelectValue,
              name: base.name || nativeCardFieldKey,
              sourceKind: 'NativeCardField' as const,
              nativeCardFieldKey,
              triggerSelectValue,
            } as AutomationTriggerFieldOption,
          ];
        }

        const workflowFieldId = rawId;
        if (!workflowFieldId) {
          return [];
        }
        if (workflowFieldId.toLowerCase() === NATIVE_CARD_FIELD_PLACEHOLDER_ID && nativeCardFieldKey) {
          const triggerSelectValue = `${NATIVE_CARD_FIELD_SELECT_PREFIX}${nativeCardFieldKey}`;
          return [
            {
              ...base,
              fieldId: triggerSelectValue,
              name: base.name || nativeCardFieldKey,
              sourceKind: 'NativeCardField' as const,
              nativeCardFieldKey,
              triggerSelectValue,
            } as AutomationTriggerFieldOption,
          ];
        }

        return [
          {
            ...base,
            fieldId: workflowFieldId,
            name: base.name || workflowFieldId,
            sourceKind: 'WorkflowField' as const,
            nativeCardFieldKey: null,
            triggerSelectValue: workflowFieldId,
          } as AutomationTriggerFieldOption,
        ];
    });
  }

  /** Alinha ao contrato de `/api/workflow-fields/workflow/:id` (mesma ideia que CustomFieldsSchemaDataService). */
  private fieldTypeFromApiNumeric(fieldType: number): CustomFieldViewModel['type'] {
    if (fieldType === 2) return 'LONG_TEXT';
    if (fieldType === 3) return 'NUMBER';
    if (fieldType === 4) return 'DATE';
    if (fieldType === 5) return 'BOOLEAN';
    if (fieldType === 6) return 'SELECT';
    if (fieldType === 7) return 'CURRENCY';
    if (fieldType === 8) return 'PROGRESS';
    if (fieldType === 9) return 'PERSON';
    if (fieldType === 10) return 'PHONE';
    if (fieldType === 11) return 'EMAIL';
    if (fieldType === 12) return 'FILE';
    if (fieldType === 13) return 'LABELS';
    if (fieldType === 14) return 'FORMULA';
    if (fieldType === 15) return 'RATING';
    if (fieldType === 17) return 'DATETIME';
    return 'TEXT';
  }

  private coalesceTrimmedString(...values: unknown[]): string {
    for (const value of values) {
      if (typeof value === 'string') {
        const t = value.trim();
        if (t) return t;
      }
    }
    return '';
  }

  private readBooleanLike(value: unknown): boolean {
    if (value === true) return true;
    if (typeof value === 'number' && value === 1) return true;
    if (typeof value === 'string') {
      const n = value.trim().toLowerCase();
      return n === 'true' || n === '1';
    }
    return false;
  }

  private normalizeFieldType(type: string): CustomFieldViewModel['type'] {
    const normalized = type.toUpperCase().trim();
    if (
      normalized === 'TEXT' ||
      normalized === 'LONG_TEXT' ||
      normalized === 'NUMBER' ||
      normalized === 'DATE' ||
      normalized === 'DATETIME' ||
      normalized === 'BOOLEAN' ||
      normalized === 'SELECT' ||
      normalized === 'CURRENCY' ||
      normalized === 'PROGRESS' ||
      normalized === 'PERSON' ||
      normalized === 'PHONE' ||
      normalized === 'EMAIL' ||
      normalized === 'FILE' ||
      normalized === 'LABELS' ||
      normalized === 'MULTI_SELECT' ||
      normalized === 'CHECKBOX' ||
      normalized === 'USER' ||
      normalized === 'FORMULA' ||
      normalized === 'RATING'
    ) {
      return normalized;
    }
    return 'TEXT';
  }

  private readApiError(error: unknown, fallback: string): string {
    if (error instanceof HttpErrorResponse) {
      const payload = this.asRecord(error.error);
      const message = this.asString(payload['message']).trim();
      if (message) return message;
      const errors = this.asArray(payload['errors'])
        .map((item) => (typeof item === 'string' ? item.trim() : ''))
        .filter(Boolean);
      if (errors.length > 0) return errors.join(' ');
    }
    return fallback;
  }

  private async loadWhatsAppTemplates(): Promise<WhatsAppTemplateModel[]> {
    try {
      return await this.automationsApi.getWhatsAppTemplates();
    } catch {
      return [];
    }
  }

  private async loadPlaceholders(workflowId: string): Promise<AutomationPlaceholdersResponse> {
    const cached = this.placeholdersSignal();
    if (
      this.placeholdersCachedWorkflowIdSignal() === workflowId &&
      cached &&
      cached.standardPlaceholders.length > 0
    ) {
      return cached;
    }
    try {
      const result = await this.automationsApi.getPlaceholders(workflowId);
      console.debug('[Automations] Placeholders loaded from API:', result);
      return result;
    } catch (err) {
      console.warn('[Automations] Failed to load placeholders, using fallback:', err);
      return { standardPlaceholders: [], customFieldPlaceholders: [] };
    }
  }

  private async loadEmailTemplates(): Promise<EmailTemplateRefModel[]> {
    try {
      const list = await this.emailTemplatesApi.list();
      return list.map((t) => ({
        key: t.key,
        name: t.name,
        isActive: t.isActive,
      }));
    } catch {
      return [];
    }
  }

  private asRecord(value: unknown): Record<string, unknown> {
    return value && typeof value === 'object' ? (value as Record<string, unknown>) : {};
  }

  private asArray(value: unknown): unknown[] {
    return Array.isArray(value) ? value : [];
  }

  private asString(value: unknown, fallback = ''): string {
    return typeof value === 'string' ? value : fallback;
  }

  private asNullableString(value: unknown): string | null {
    return typeof value === 'string' ? value : null;
  }

  private asBoolean(value: unknown, fallback = false): boolean {
    return typeof value === 'boolean' ? value : fallback;
  }

  private asNumber(value: unknown, fallback = 0): number {
    return typeof value === 'number' ? value : fallback;
  }

  private normalizeColorHex(value: string): string {
    const normalized = value.trim().toUpperCase();
    return /^#[0-9A-F]{6}$/.test(normalized) ? normalized : '#E2E8F0';
  }

  private async syncAutomationConditions(
    workflowId: string,
    automationId: string,
    existingConditions: WorkflowAutomationConditionModel[],
    triggerType: WorkflowAutomationModel['triggerType'],
    triggerConfigJson: Record<string, unknown> | null,
    desiredConditions: CreateWorkflowAutomationConditionRequestModel[],
  ): Promise<WorkflowAutomationConditionModel[]> {
    const triggerManagedConditions = this.buildTriggerManagedConditions(
      triggerType,
      triggerConfigJson
    );
    const allDesiredConditions = [...triggerManagedConditions, ...desiredConditions];

    for (const condition of existingConditions) {
      if (!condition.id) continue;
      await this.conditionsApi.delete(workflowId, automationId, condition.id).catch(() => {});
    }

    const createdConditions: WorkflowAutomationConditionModel[] = [];
    for (const condition of allDesiredConditions) {
      const created = await this.conditionsApi.create(workflowId, automationId, condition);
      if (created) {
        createdConditions.push(created);
      }
    }

    if (desiredConditions.length === 0) {
      return [];
    }

    if (createdConditions.length > 0) {
      return createdConditions.filter(
        (condition) =>
          !this.isTriggerManagedCondition(triggerType, triggerConfigJson, condition)
      );
    }

    const fallback = await this.conditionsApi
      .list(workflowId, automationId)
      .catch(() => ({ items: [] as WorkflowAutomationConditionModel[] }));
    return fallback.items.filter(
      (condition) => !this.isTriggerManagedCondition(triggerType, triggerConfigJson, condition)
    );
  }

  private countInvalidConditions(conditions: WorkflowAutomationConditionModel[]): number {
    return conditions.filter((condition) => !isConditionValid(condition)).length;
  }

  private buildTriggerManagedConditions(
    triggerType: WorkflowAutomationModel['triggerType'],
    triggerConfigJson: Record<string, unknown> | null
  ): CreateWorkflowAutomationConditionRequestModel[] {
    if (triggerType !== 'CardMovedToStage') {
      return [];
    }

    const stageId = this.readTriggerStageId(triggerConfigJson);
    if (!stageId) {
      return [];
    }

    return [
      {
        field: 'CardStage',
        operator: 'Equals',
        value: stageId,
      },
    ];
  }

  private isTriggerManagedCondition(
    triggerType: WorkflowAutomationModel['triggerType'],
    triggerConfigJson: Record<string, unknown> | null,
    condition: Pick<WorkflowAutomationConditionModel, 'field' | 'operator' | 'value'>
  ): boolean {
    if (triggerType !== 'CardMovedToStage') {
      return false;
    }

    const stageId = this.readTriggerStageId(triggerConfigJson);
    if (!stageId) {
      return false;
    }

    return (
      condition.field === 'CardStage'
      && condition.operator === 'Equals'
      && typeof condition.value === 'string'
      && condition.value.trim() === stageId
    );
  }

  private async hydrateMissingTriggerConfigs(
    workflowId: string,
    automations: WorkflowAutomationModel[]
  ): Promise<WorkflowAutomationModel[]> {
    const pending = automations.filter(
      (automation) =>
        automation.triggerType === 'CardMovedToStage'
        && !this.readTriggerStageId(automation.triggerConfigJson)
    );
    if (pending.length === 0) {
      return automations;
    }

    const conditionsByAutomation = await Promise.all(
      pending.map(async (automation) => {
        try {
          const response = await this.conditionsApi.list(workflowId, automation.id);
          return [automation.id, response.items] as const;
        } catch {
          return [automation.id, [] as WorkflowAutomationConditionModel[]] as const;
        }
      })
    );

    const byId = new Map<string, WorkflowAutomationConditionModel[]>(
      conditionsByAutomation.map(([automationId, conditions]) => [automationId, conditions])
    );

    return automations.map((automation) =>
      this.mergeTriggerConfigFromConditions(automation, byId.get(automation.id) ?? automation.conditions)
    );
  }

  private mergeTriggerConfigFromConditions(
    automation: WorkflowAutomationModel,
    conditions: WorkflowAutomationConditionModel[]
  ): WorkflowAutomationModel {
    if (automation.triggerType !== 'CardMovedToStage') {
      return automation;
    }

    const existingStageId = this.readTriggerStageId(automation.triggerConfigJson);
    if (existingStageId) {
      return automation;
    }

    const stageCondition = conditions.find(
      (condition) =>
        condition.field === 'CardStage'
        && condition.operator === 'Equals'
        && typeof condition.value === 'string'
        && condition.value.trim().length > 0
    );
    const stageId =
      stageCondition && typeof stageCondition.value === 'string'
        ? stageCondition.value.trim()
        : '';
    if (!stageId) {
      return automation;
    }

    return {
      ...automation,
      triggerConfigJson: {
        ...(automation.triggerConfigJson ?? {}),
        targetStageId: stageId,
      },
    };
  }

  private readTriggerStageId(config: Record<string, unknown> | null): string {
    const value = config?.['targetStageId'];
    return typeof value === 'string' ? value.trim() : '';
  }
}
